import { css, cx, useTheme, FableTheme } from '@fable/theme'
import CloseIcon from '../../assets/icons/components/CloseIcon'
import { Button, ButtonProps, Circle, Text } from '@fable/components'
import { useSidebar } from '../../hooks'
import ErrorBanner from '../error_banner/ErrorBanner'
import { SidebarButtons } from './SidebarButtons'

export interface SideBarButtons extends ButtonProps {
  component?: React.ReactNode
}

interface Props extends React.HTMLProps<HTMLDivElement> {
  children: React.ReactNode | React.ReactNode[]
  title: string
  buttons?: SideBarButtons[] | null
  onClickClose?: () => void
}

const Sidebar: React.VFC<Props> = ({
  children,
  title,
  buttons,
  onClickClose,
  ...nativeProps
}) => {
  const { isSidebarActive, onCloseSidebar } = useSidebar()
  const theme = useTheme()
  const styles = useStyles(theme, buttons)

  const handleClickClose = () => {
    if (onClickClose) onClickClose()
    else onCloseSidebar()
  }

  const renderCloseButton = () => (
    <Button
      negative
      secondary
      onClick={handleClickClose}
      dataTestId="sidebar-close"
      className={styles.closeButton}
    >
      <Circle diameter="24px">
        <CloseIcon />
      </Circle>
    </Button>
  )

  return (
    <div
      className={cx(
        styles.wrapper,
        isSidebarActive && styles.active,
        nativeProps.className,
        'fds-ui--sidebar-wrapper'
      )}
      onClick={isSidebarActive ? handleClickClose : () => null}
    >
      <div
        className={cx(styles.sidebar, 'fds-ui--sidebar')}
        onClick={(e) => e.stopPropagation()}
      >
        <ErrorBanner />
        <div className={styles.header}>
          <Text type="subhead" sizing="S" tag="h5">
            {title}
          </Text>
          {renderCloseButton()}
        </div>
        <div className={styles.content}>
          {children}
          <SidebarButtons buttons={buttons} />
        </div>
      </div>
    </div>
  )
}

export default Sidebar

const useStyles = (theme: FableTheme, buttons?: ButtonProps[] | null) => {
  const { mediaQueries, colors, motion } = theme

  return {
    wrapper: css`
      width: 100%;
      height: 100%;
      position: fixed;
      display: flex;
      align-items: flex-end;
      top: 0;
      right: 0;
      z-index: 99999;
      pointer-events: none;
      background-color: transparent;
      ${motion.transition('background-color', 'ease')};
      ${mediaQueries.mobileL} {
        align-items: center;
        justify-content: center;
      }
      ${mediaQueries.tablet} {
        justify-content: flex-end;
        top: 60px;
      }
    `,
    sidebar: css`
      padding-top: 10px;
      width: 100%;
      height: 100%;
      max-height: calc(100% - 112px);
      background: ${colors.whiteFang};
      box-shadow: 0px 6px 4px rgba(0, 0, 0, 0.25);
      overflow: hidden;
      border-radius: 12px;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      transform: translate3d(0, 100vh, 0);
      opacity: 0;
      ${motion.transition()};

      ${mediaQueries.mobileL} {
        max-width: 508px;
        border-radius: 12px;
        transform: translate3d(0, 100vh, 0);
      }

      ${mediaQueries.tablet} {
        max-height: 100%;
        width: fit-content;
        height: 100%;
        max-width: 512px;
        border-radius: 0px;
        transform: translate3d(100vw, 0, 0);
      }
    `,
    content: css`
      padding-bottom: ${buttons ? 120 * buttons.length : '24'}px;
      height: calc(100vh - 100px);
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
    `,
    header: css`
      height: 22px;
      margin: 8px 0px;
      text-align: center;
      position: relative;
    `,
    closeButton: css`
      position: absolute;
      width: fit-content;
      height: fit-content;
      padding: 0;
      top: -2px;
      right: 12px;
      ${motion.transition('background-color', 'ease')};
      > div {
        display: flex;
        justify-content: center;
        align-items: center;
        > svg {
          width: 20px;
          height: 20px;
        }
      }
    `,
    active: css`
      background-color: ${colors.blackSwan}80;
      pointer-events: auto;
      .fds-ui--sidebar {
        opacity: 1;
        transform: translate3d(0, 0, 0);
      }
    `,
  }
}
